import _ from 'lodash';
import {
  INIT_MAIN,
  TOGGLE_OPTION,
  TOGGLE_OPTION_SUCCESS,
  TOGGLE_OPTION_ERROR,
  TOGGLE_OPTION_CONFLICT,
  TOGGLE_OPTION_CONFLICT_RESOLVE,
  TOGGLE_OPTION_CONFLICT_CANCEL,
  UPDATE_CONFIG,
  UPDATE_CONFIG_SUCCESS,
  UPDATE_CONFIG_ERROR,
  UPDATE_POSTAL_CODE,
  START_OVER,
  START_OVER_SUCCESS,
  IS_SELECTION_REVEALED_CHANGED,
  IS_BODY_TYPE_REVEALED_CHANGED,
  SAVED_BUILD_COUNT,
  IS_LEGAL_DISCLOSURE_OPENED_CHANGED,
} from '../actionTypes';
import { LOCATION_COOKIE } from '../../constants';
import { normalizeStyle } from '../../resources/normalizeHelpers/normalizeHelpers.constant';
import { putCookieObject } from '../../utils/cookieHelpers/cookieHelpers';
import {
  clearAllByModel,
  CONFIG,
  getAllSavedBuilds,
  getByModelAndKey,
  setByModelAndKey,
} from '../../resources/localStorage/localStorage.service';
import { getModelParams } from '../../common/model/utils';
import fetchIncentives from '../../api/fetchIncentives';
import { fetchTrimMatrixWithState } from '../../api/fetchTrimMatrix';

import {
  fetchByStyleIdAndSerializedState,
  fetchConfig,
  fetchToggleOption,
} from '../../api/fetchConfig';
import {
  TRIMS as TRIMS_STEP,
  CONFIG as CONFIG_STEP,
  MODELS as MODELS_STEP,
} from '../../common/steps';
import { trackTargetUpdated } from '../../factories/analytics/analytics.service';

const setConfig = (config, options) => {
  const existing = getByModelAndKey(window.appResources, CONFIG);
  const configToSave = _.assign(
    existing,
    {
      _lastModified: new Date(),
      ss: config.ss,
      styleId: config.styleId,
      bodyStyleTitle: window.appResources.bodyStyleTitle,
    },
    options,
    getModelParams()
  );
  setByModelAndKey(window.appResources, CONFIG, configToSave);

  const wasOptionToggled = _.get(options, 'toggled');
  wasOptionToggled && trackTargetUpdated();
};

const createNormalizeConfigWithDisclosures = (disclosures) => (config) => {
  config.styles = config.styles.map((style) => {
    normalizeStyle(style, disclosures);
    return style;
  });
  return config;
};

const fetchIncentivesForConfig = (options) => (config) => {
  const { v3ServicesPath, modelPath, postalCode } = options;

  const incentivesPromise = fetchIncentives({
    v3ServicesPath,
    modelPath,
    postalCode,
    styleId: config.styleId,
    rpos: config.selections.rpos,
  });

  return incentivesPromise.then((incentives) => ({
    config,
    incentives,
  }));
};

const getFullConfig = (params, localState) => {
  const { styleId, ss, postalCode } = params;

  if (styleId && ss) {
    return fetchByStyleIdAndSerializedState(styleId, ss.replace(/\s/g, '+'), {
      postalCode,
    });
  }

  if (styleId) {
    return fetchConfig({ postalCode, styleId });
  }

  if (localState) {
    return fetchByStyleIdAndSerializedState(localState.styleId, localState.ss, {
      postalCode,
    });
  }

  return fetchConfig({ postalCode, styleId });
};

export const fetchInitialConfig = (params, onSuccess, onError) => {
  return (dispatch, getState) => {
    const state = getState();
    const { disclosures, appResources } = state;
    const localState = getByModelAndKey(appResources, CONFIG);

    const fullConfigPromise = getFullConfig(params, localState)
      .then(createNormalizeConfigWithDisclosures(disclosures))
      .then(fetchIncentivesForConfig({ ...state.api, ...params }));

    const matrixPromise = fetchTrimMatrixWithState(state);

    Promise.all([fullConfigPromise, matrixPromise]).then(
      ([{ config, incentives }, matrix]) => {
        setConfig(config);
        dispatch({
          type: INIT_MAIN,
          payload: {
            fullConfig: config,
            incentives,
            matrix,
          },
        });

        const { styleId, hideReveal } = params;
        if (styleId && !hideReveal) {
          dispatch(setBodyTypeRevealState());
          dispatch(setSelectionRevealState());
        }
        onSuccess(config);
      },
      (err) => {
        onError(err);
      }
    );
  };
};

export const toggleOptionConflictResolve = (option) => {
  return (dispatch, getState) => {
    const { params, conflictConfig: config, api } = getState();
    setConfig(config, { toggled: true });

    fetchIncentivesForConfig({ ...api, ...params })(config).then(
      ({ config, incentives }) => {
        dispatch({
          type: TOGGLE_OPTION_CONFLICT_RESOLVE,
          payload: {
            fullConfig: config,
            option,
            incentives,
          },
        });
      }
    );
  };
};

export const toggleOptionConflictCancel = () => ({
  type: TOGGLE_OPTION_CONFLICT_CANCEL,
});

export const toggleOption = (option) => {
  return (dispatch, getState) => {
    const state = getState();
    const { disclosures, conflictConfig, params, api } = state;
    const config = conflictConfig || state.config;

    dispatch({ type: TOGGLE_OPTION });

    fetchToggleOption(config.styleId, config.ss, option.code, {
      postalCode: params.postalCode,
    })
      .then(createNormalizeConfigWithDisclosures(disclosures))
      .then(
        (config) => {
          if (config.hasConflict) {
            dispatch({
              type: TOGGLE_OPTION_CONFLICT,
              payload: {
                option,
                fullConfig: config,
              },
            });
            return;
          }

          return fetchIncentivesForConfig({ ...api, ...params })(config).then(
            ({ config, incentives }) => {
              setConfig(config, { toggled: true });

              dispatch({
                type: TOGGLE_OPTION_SUCCESS,
                payload: {
                  fullConfig: config,
                  option,
                  incentives,
                },
              });
            }
          );
        },
        () => {
          dispatch({ type: TOGGLE_OPTION_ERROR });
        }
      );
  };
};

const updateConfig = (selections) => {
  return (dispatch, getState) => {
    const { params, disclosures, api } = getState();

    dispatch({ type: UPDATE_CONFIG });

    fetchConfig({ postalCode: params.postalCode, ...selections })
      .then(createNormalizeConfigWithDisclosures(disclosures))
      .then(fetchIncentivesForConfig({ ...api, ...params }))
      .then(
        ({ config, incentives }) => {
          setConfig(config, { toggled: false });
          dispatch({
            type: UPDATE_CONFIG_SUCCESS,
            payload: {
              fullConfig: config,
              incentives,
            },
          });
        },
        () => {
          dispatch({ type: UPDATE_CONFIG_ERROR });
        }
      );
  };
};

export const updateConfigWithOption = (type, option) => {
  return (dispatch, getState) => {
    const { config } = getState();
    const existingSelections = config.selections;

    let selections = _.pick(existingSelections, [
      'axleRatio',
      'bodyTypeId',
      'driveType',
      'engine',
      'styleId',
      'transmission',
    ]);
    selections[type] = option.id;

    dispatch(updateConfig(selections));
  };
};

export const updateConfigWithSelections = (selections) =>
  updateConfig(selections);

export const updateConfigWithStyleIdAndSerializedState = (
  styleId,
  ss,
  options,
  revealType
) => {
  return (dispatch, getState) => {
    dispatch({ type: UPDATE_CONFIG });
    const { params, disclosures, api } = getState();

    fetchByStyleIdAndSerializedState(styleId, ss, {
      postalCode: params.postalCode,
    })
      .then(createNormalizeConfigWithDisclosures(disclosures))
      .then(fetchIncentivesForConfig({ ...api, ...params }))
      .then(
        ({ config, incentives }) => {
          setConfig(config, {
            toggled: false,
            ...options,
          });
          dispatch({
            type: UPDATE_CONFIG_SUCCESS,
            payload: {
              fullConfig: config,
              incentives,
              wasTrimOptionToggled: _.get(options, 'toggledTrim', false),
            },
          });

          switch (revealType) {
            case MODELS_STEP:
            case CONFIG_STEP:
              dispatch(setBodyTypeRevealState());
              break;
            case TRIMS_STEP:
              dispatch(setSelectionRevealState());
              break;
          }
        },
        () => {
          dispatch({ type: UPDATE_CONFIG_ERROR });
        }
      );
  };
};

export const updatePostalCode = (postalCode, data, isSummary, cb) => {
  return (dispatch, getState) => {
    const state = getState();
    const { config, disclosures, api } = state;

    putCookieObject(LOCATION_COOKIE, data);

    dispatch({
      type: UPDATE_POSTAL_CODE,
      payload: { postalCode },
    });

    const configAndIncentivesPromise = fetchByStyleIdAndSerializedState(
      config.styleId,
      config.ss,
      { postalCode }
    )
      .then(createNormalizeConfigWithDisclosures(disclosures))
      .then((config) => {
        return fetchIncentivesForConfig({
          ...api,
          ...state.params,
          postalCode,
          forceIncentives: isSummary,
        })(config);
      });

    const matrixPromise = fetchTrimMatrixWithState(state, { postalCode });

    Promise.all([configAndIncentivesPromise, matrixPromise]).then(
      ([{ config, incentives }, matrix]) => {
        setConfig(config, { toggled: false });
        dispatch({
          type: UPDATE_CONFIG_SUCCESS,
          payload: {
            fullConfig: config,
            incentives,
            matrix,
          },
        });

        if (cb) {
          cb();
        }
      }
    );
  };
};

export const startOver = () => {
  return (dispatch, getState) => {
    const { disclosures, params, appResources, api } = getState();

    clearAllByModel(appResources);
    dispatch({ type: START_OVER });

    fetchConfig({ postalCode: params.postalCode })
      .then(createNormalizeConfigWithDisclosures(disclosures))
      .then(fetchIncentivesForConfig({ ...api, ...params }))
      .then(({ config, incentives }) => {
        setConfig(config);
        dispatch({
          type: START_OVER_SUCCESS,
          payload: {
            fullConfig: config,
            incentives,
          },
        });
      });
  };
};

export const setSelectionRevealState = () => {
  return (dispatch, getState) => {
    const { isSelectionRevealed } = getState();
    if (!isSelectionRevealed) {
      const existing = getByModelAndKey(window.appResources, CONFIG);
      const configToSave = _.assign(existing, {
        _lastModified: new Date(),
        isSelectionRevealed: true,
      });
      setByModelAndKey(window.appResources, CONFIG, configToSave);

      dispatch({
        type: IS_SELECTION_REVEALED_CHANGED,
        payload: true,
      });
    }
  };
};

export const setBodyTypeRevealState = () => {
  return (dispatch, getState) => {
    const { isBodyTypeRevealed } = getState();
    if (!isBodyTypeRevealed) {
      const existing = getByModelAndKey(window.appResources, CONFIG);
      const configToSave = _.assign(existing, {
        _lastModified: new Date(),
        isBodyTypeRevealed: true,
      });
      setByModelAndKey(window.appResources, CONFIG, configToSave);

      dispatch({
        type: IS_BODY_TYPE_REVEALED_CHANGED,
        payload: true,
      });
    }
  };
};

export const setSavedBuildCount = () => {
  return (dispatch) => {
    const { brand, country } = window.appResources;
    const count = getAllSavedBuilds(brand, country).length;

    dispatch({
      type: SAVED_BUILD_COUNT,
      payload: count,
    });
  };
};

export const setLegalDisclosureOpenedState = (isOpened) => {
  return (dispatch) => {
    dispatch({
      type: IS_LEGAL_DISCLOSURE_OPENED_CHANGED,
      payload: isOpened,
    });
  };
};
